import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagDirective } from '@neutrinos/ngx-feature-flags';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';
import { BranchStateService } from '../services/branch-state.service';
import { Branch, BranchService } from '../services/branch.service';
import { ProjectService } from '../services/project.service';
import { BranchButtonComponent } from './branch-button.component';

@Component({
    selector: 'platform-dev-mode-banner',
    standalone: true,
    imports: [OverlayPanelModule, InputTextModule, PUITranslatePipe, BranchButtonComponent, FeatureFlagDirective],
    template: `
        @if (isDevMode()) {
            <div
                class="flex align-items-center justify-content-between px-3 py-2 height-40 font-family-sans border-bottom-1 border-200"
                style="background-color: var(--alpha-primary-color);"
            >
                <div>
                    <div class="flex gap-3 align-items-center">
                        <div class="flex align-items-center justify-content-center">
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect
                                    x="4"
                                    y="-0.200195"
                                    width="6"
                                    height="6"
                                    rx="1"
                                    transform="rotate(45 4 -0.200195)"
                                    fill="var(--alpha-background-surface-color)"
                                />
                            </svg>
                        </div>
                        <div class="flex align-items-center justify-content-center">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.00016 14.6663C11.6668 14.6663 14.6668 11.6663 14.6668 7.99967C14.6668 4.33301 11.6668 1.33301 8.00016 1.33301C4.3335 1.33301 1.3335 4.33301 1.3335 7.99967C1.3335 11.6663 4.3335 14.6663 8.00016 14.6663Z"
                                    stroke="var(--alpha-background-surface-color)"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M8 5V8"
                                    stroke="var(--alpha-background-surface-color)"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <path
                                    d="M7.99609 10.667H8.00208"
                                    stroke="var(--alpha-background-surface-color)"
                                    stroke-width="1.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <div
                            class="flex font-normal text-base align-items-center gap-1"
                            style="color: var(--alpha-background-surface-color);"
                        >
                            <div>
                                <b>{{ 'default-dev-mode-warning-title' | pTranslate: 'Development Mode Active' }}</b>
                            </div>
                            <div>:</div>
                            <div>
                                {{
                                    'default-dev-mode-warning-summary'
                                        | pTranslate: 'Optimizations disabled. Running on'
                                }}
                            </div>
                        </div>
                    </div>
                </div>

                <div *featureFlag="'CHANGE_TRACKING'" class="flex align-items-center">
                    <!-- Branch selector -->
                    <platform-branch-button
                        [label]="currentBranch()"
                        size="md"
                        (buttonClick)="branchPopover.toggle($event)"
                    />

                    <p-overlayPanel
                        #branchPopover
                        [appendTo]="'body'"
                        styleClass="branch-popover"
                        (onShow)="onPopoverShow()"
                    >
                        <div class="branch-popover-content">
                            <div class="branch-search">
                                <span class="p-input-icon-left w-full">
                                    <i class="pi pi-search"></i>
                                    <input
                                        #searchInput
                                        type="text"
                                        pInputText
                                        placeholder="Search branches..."
                                        [value]="searchQuery()"
                                        (input)="onSearchInput($event)"
                                        class="w-full"
                                    />
                                </span>
                            </div>

                            <div class="branch-list" role="listbox">
                                @if (loading()) {
                                    <div class="branch-loading">Loading...</div>
                                } @else if (branches().length === 0) {
                                    <div class="branch-empty">No branches found</div>
                                } @else {
                                    @for (branch of branches(); track branch.name) {
                                        <div
                                            class="branch-item"
                                            [class.selected]="branch.name === currentBranch()"
                                            role="option"
                                            tabindex="0"
                                            [attr.aria-selected]="branch.name === currentBranch()"
                                            (click)="selectBranch(branch.name)"
                                            (keydown.enter)="selectBranch(branch.name)"
                                        >
                                            <i class="pi pi-share-alt"></i>
                                            <span class="branch-item-name">{{ branch.name }}</span>
                                            @if (branch.name === currentBranch()) {
                                                <i class="pi pi-check branch-selected-icon"></i>
                                            }
                                        </div>
                                    }
                                }
                            </div>
                        </div>
                    </p-overlayPanel>
                </div>
            </div>
        }
    `,
    styles: [
        `
            .branch-popover-content {
                width: 280px;
            }

            .branch-search {
                padding: 8px;
                border-bottom: 1px solid #dee2e6;
            }

            .branch-list {
                max-height: 300px;
                overflow-y: auto;
                padding: 4px;
            }

            .branch-item {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.15s ease;
            }

            .branch-item:hover {
                background: #f8f9fa;
            }

            .branch-item.selected {
                background: #eff6ff;
                color: #1d4ed8;
            }

            .branch-item-name {
                flex: 1;
                font-size: 14px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .branch-selected-icon {
                color: #3b82f6;
            }

            .branch-loading,
            .branch-empty {
                padding: 16px;
                text-align: center;
                color: #6c757d;
                font-size: 14px;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevModeBannerComponent implements OnInit {
    private projectService = inject(ProjectService);
    private branchService = inject(BranchService);
    private branchStateService = inject(BranchStateService);
    private destroyRef = inject(DestroyRef);

    readonly branchPopover = viewChild<OverlayPanel>('branchPopover');
    readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

    readonly isDevMode = signal<boolean>(false);
    readonly currentBranch = this.branchStateService.currentBranch;
    readonly branches = signal<Branch[]>([]);
    readonly searchQuery = signal<string>('');
    readonly loading = signal<boolean>(false);

    private searchSubject = new Subject<string>();

    ngOnInit(): void {
        const project = this.projectService.fetchCurrentProject();
        this.isDevMode.set(project?.devMode ?? false);

        // Debounced server-side search
        this.searchSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((query) => this.loadBranches(query)),
            )
            .subscribe();
    }

    onSearchInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchQuery.set(value);
        this.searchSubject.next(value);
    }

    selectBranch(branchName: string): void {
        this.branchPopover()?.hide();

        // Full page reload with branch query param
        const url = new URL(window.location.href);
        url.searchParams.set('branch', branchName);
        window.location.href = url.toString();
    }

    onPopoverShow(): void {
        // Reset search and reload branches when popover opens
        this.searchQuery.set('');
        this.loadBranches('').subscribe();
        // Focus search input
        setTimeout(() => this.searchInput()?.nativeElement.focus());
    }

    private loadBranches(filter: string) {
        const project = this.projectService.fetchCurrentProject();
        if (!project?.projectId) {
            return EMPTY;
        }

        this.loading.set(true);
        return this.branchService
            .listBranches({
                projectId: project.projectId,
                branchFilter: filter || undefined,
                limit: 20,
            })
            .pipe(
                tap((response) => {
                    this.branches.set(response.branches);
                    this.loading.set(false);
                }),
                catchError(() => {
                    this.loading.set(false);
                    this.branches.set([]);
                    return EMPTY;
                }),
            );
    }
}
